import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { subscribeToClassRidesPulled } from "../local/rideCacheEvents";

// Card #3144 -- "Withdrawn" (#2795, DR124.5), the presiding judge (judge
// position C) or an admin only. Modeled directly on useRideExcused (#2064) and
// useRideNoShow (#1992): Withdrawn is a RIDE-level fact (score-api's
// ride.withdrawn, not one judge's own ScoreSheet), so every judge position
// scoring the SAME ride must see the SAME mark, delivered by the 5s change check (#3200: one fetch on open, then the class
// re-pull; GET /ride/:rideId/withdrawn is no longer polled). Before this hook
// existed, a Withdrawn raised at C (#2798's End Ride) reached no other seat:
// each kept a live, signable sheet for a ride that was over.
//
// mark()/undo() are DIRECT writes, not queued through the offline sync
// engine, for the same reason as No Show / Excused (see useRideExcused).

/**
 * @param {string} rideId
 * @returns {{
 *   withdrawn: object|null,
 *   loading: boolean,
 *   error: Error|null,
 *   mark: () => Promise<void>,
 *   undo: () => Promise<void>,
 *   marking: boolean,
 *   markError: Error|null,
 * }}
 */
// card #3196 - `online` (default true) skips the fetch while offline: these are
// live, shared ride facts with nothing local to read, so offline keeps the last
// value and makes no request; the online flip re-runs the effect and refreshes.
export function useRideWithdrawn(rideId, { online = true } = {}) {
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const scoreApi = useScoreApi();
  const [withdrawn, setWithdrawn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;

  // Same stale-response guard as useRideNoShow: a fetch that started
  // before a mark()/undo() lands must not clobber the fresher state with
  // its own late-arriving read.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    if (!onlineRef.current) {
      setLoading(false);
      return;
    }
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      const result = unwrap(await apiRef.current.rides.getRideWithdrawn(id)) ?? null;
      if (isStale()) return;
      setWithdrawn(result);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError(err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  // card #3200 - one fetch when the sheet opens (and when connectivity
  // returns), NO interval. After that the value arrives with the class re-pull
  // the 5s change check does when any ride in this class changes: the pulled
  // ride row carries `withdrawn`, so no per-ride request is needed (see
  // local/rideCacheEvents.js).
  useEffect(() => {
    if (!rideId) {
      setWithdrawn(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    refresh();
    return undefined;
  }, [rideId, refresh, online]);

  useEffect(
    () =>
      subscribeToClassRidesPulled(({ rides: pulled }) => {
        const id = rideIdRef.current;
        if (!id) return;
        const row = pulled.find((r) => r.id === id);
        if (!row) return;
        // supersedes any fetch still in flight
        refreshGenRef.current += 1;
        setWithdrawn(row.withdrawn ?? null);
        setError(null);
        setLoading(false);
      }),
    [],
  );

  const mark = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      const result = unwrap(await apiRef.current.rides.markRideWithdrawn(id));
      setWithdrawn(result);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  const undo = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      await apiRef.current.rides.undoRideWithdrawn(id);
      setWithdrawn(null);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  return { withdrawn, loading, error, mark, undo, marking, markError };
}

export default useRideWithdrawn;
